// Settings Module Tour
// Platform configuration, integrations, and customization

export const settingsTour = {
  id: 'settings',
  name: 'Settings Module Tour',
  displayName: 'Configure Your Platform',
  description: 'Set up integrations, customize fields, and manage your team',
  estimatedDuration: '5 minutes',
  category: 'feature',
  autoStart: false,
  prerequisiteTours: ['ftux'],
  
  config: {
    animate: true,
    overlayOpacity: 0.75,
    smoothScroll: true,
    allowClose: true,
    showProgress: true
  },
  
  steps: [
    // Step 1: Welcome to Settings
    {
      element: '[href*="settings"], button:contains("Settings")',
      popover: {
        title: '⚙️ Welcome to Settings',
        description: `
          This is mission control for your platform. Here you'll connect phone numbers, 
          email domains, payment processors, and more. Think of Settings as the foundation—
          set it up right once, and everything else just works!
        `,
        side: 'right',
        align: 'center'
      }
    },

    // Step 2: Settings Categories
    {
      popover: {
        title: '🗂️ Settings Sections',
        description: `
          Settings is organized into categories:
          • **Business Profile**: Your company info, logo, address
          • **Phone Numbers**: Buy or port numbers for calls/SMS
          • **Email Services**: Connect sending domains
          • **Domains**: Connect custom domains for funnels
          • **Integrations**: Connect other tools (Zapier, Stripe, etc.)
          • **Team**: Add users and set permissions
          • **Custom Fields**: Create fields for your specific needs
          • **API**: Developer access for custom integrations
          
          Let's explore the most important ones!
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 3: Business Profile
    {
      element: '[href*="business"], [href*="profile"]',
      popover: {
        title: '🏢 Business Profile',
        description: `
          Start here! Set up your company information:
          • Business name
          • Logo (appears on emails, booking pages, etc.)
          • Physical address (required for email compliance)
          • Time zone
          • Contact info
          
          This info appears throughout the platform and on client-facing pages.
        `,
        side: 'left',
        align: 'start',
        proTip: 'Upload a square logo (at least 500x500px) for best display across all features!'
      }
    },

    // Step 4: Phone Numbers
    {
      element: '[href*="phone"]',
      popover: {
        title: '📱 Phone Number Setup',
        description: `
          To send/receive SMS and make calls, you need a phone number:
          • **Buy a new number**: Search by area code, $1-2/month
          • **Port existing number**: Transfer your current number
          • **Toll-free numbers**: 1-800 numbers available
          
          Each number can handle SMS, voice, or both. You can have multiple 
          numbers for different purposes (support, sales, etc.)
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 5: Email Services
    {
      element: '[href*="email-service"], [href*="mailgun"]',
      popover: {
        title: '📧 Email Service Setup',
        description: `
          To send marketing emails from your domain (instead of generic):
          1. Add your domain (e.g., yourbusiness.com)
          2. Update DNS records at your domain registrar
          3. Verify the domain
          4. Set "From Name" and "From Email"
          
          This ensures emails come from you@yourbusiness.com, which looks 
          professional and improves deliverability (fewer spam folders!).
        `,
        side: 'left',
        align: 'center',
        proTip: 'Use a subdomain like mail.yourbusiness.com for marketing to protect your main domain reputation!'
      }
    },

    // Step 6: Domains (for Funnels)
    {
      element: '[href*="domain"]',
      popover: {
        title: '🌐 Connect Custom Domains',
        description: `
          Make your funnels look professional with your own domain:
          • Add domain here (e.g., www.yourbusiness.com)
          • Update DNS records at registrar
          • Verify connection
          • Assign to specific funnels
          
          Now your funnels live at yourbusiness.com/offer instead of 
          generic-123.myhighlevel.link. Much more professional!
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 7: Integrations
    {
      element: '[href*="integration"], [href*="connect"]',
      popover: {
        title: '🔌 Connect Other Tools',
        description: `
          Connect the tools you already use:
          • **Stripe/PayPal**: Accept payments
          • **Zapier**: Connect to 3,000+ apps
          • **Google/Outlook Calendar**: Sync appointments
          • **Facebook/Instagram**: Manage social messaging
          • **QuickBooks**: Sync accounting
          • **Zoom**: Automatic meeting links
          
          These integrations let data flow between systems automatically!
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 8: Custom Fields
    {
      element: '[href*="custom-field"]',
      popover: {
        title: '🔧 Create Custom Fields',
        description: `
          Standard contact fields (name, email, phone) are great, but you 
          probably need industry-specific fields:
          • Real estate: "Budget Range", "Preferred Neighborhoods"
          • Coaching: "Biggest Challenge", "Goal Timeline"
          • Services: "Service Interest", "Referral Source"
          
          Create custom fields, then use them in forms, workflows, and Smart Lists!
        `,
        side: 'left',
        align: 'center',
        proTip: 'Use dropdown fields instead of text when possible—it keeps data clean and makes filtering easier!'
      }
    },

    // Step 9: Team Management
    {
      element: '[href*="team"], [href*="user"]',
      popover: {
        title: '👥 Add Team Members',
        description: `
          Working with others? Add team members:
          • Invite by email
          • Set permissions (admin, user, limited)
          • Assign to calendars
          • Control what they can see/edit
          
          Each person gets their own login. Track who does what, assign conversations, 
          and collaborate seamlessly!
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 10: Notifications
    {
      element: '[href*="notification"]',
      popover: {
        title: '🔔 Configure Notifications',
        description: `
          Choose what alerts you want to receive:
          • New contacts
          • Form submissions
          • Appointment bookings
          • Opportunity changes
          • Workflow completions
          
          Get notified via email, SMS, or in-app. Customize frequency 
          (instant, daily digest, etc.) to avoid notification overload!
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 11: API & Webhooks
    {
      element: '[href*="api"], [href*="webhook"]',
      popover: {
        title: '⚙️ Developer Options',
        description: `
          For technical users or developers:
          • **API Access**: Programmatically interact with platform
          • **Webhooks**: Send data to external systems in real-time
          • **API Key**: Generate keys for authentication
          
          Use these for custom integrations or connecting to proprietary systems. 
          Check the API documentation for endpoints and examples.
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 12: Billing & Subscription
    {
      element: '[href*="billing"], [href*="subscription"]',
      popover: {
        title: '💳 Manage Your Subscription',
        description: `
          View and manage your account:
          • Current plan and features
          • Usage (contacts, emails sent, SMS, etc.)
          • Billing history
          • Update payment method
          • Upgrade/downgrade plan
          
          Keep an eye on usage to avoid hitting limits during important campaigns!
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 13: Completion
    {
      popover: {
        title: '🎉 You\'re a Settings Pro!',
        description: `
          Great work! You now know how to:
          ✅ Set up your business profile
          ✅ Connect phone numbers for SMS/calls
          ✅ Configure email sending domains
          ✅ Add custom domains for funnels
          ✅ Integrate with other tools
          ✅ Create custom fields
          ✅ Manage your team
          
          **Next Steps:**
          • Complete your business profile with logo and address
          • Buy a phone number if you plan to use SMS
          • Connect your domain for professional branding
          • Add team members if collaborating
          
          Your platform is now fully configured! 🎊
        `,
        side: 'over',
        align: 'center',
        showButtons: ['close'],
        doneBtnText: 'All Set! →'
      }
    }
  ]
};
